import Landing from '@/ui/components/portal-preview/pages/landing/landing'
import RefundMethods from '@/ui/components/portal-preview/pages/refund-methods/refund-method-page'
import ReturnMethods from '@/ui/components/portal-preview/pages/return-methods/return-methods.page'
import iphoneMockup from '@/assets/images/iphoneMockup.png'
import React from 'react'
import { PreviewDevice } from './preview-contexts'
import { usePortalBranding } from './use-portal-branding'

export const portalViews = {
  landing: Landing,
  refundMethods: RefundMethods,
  returnMethods: ReturnMethods
}

const FRAME_WIDTH = 300

// Measured from iphoneMockup.png (1450x2936): the screen is a transparent hole in the frame, at
// x 80..1369 and y 76..2859. Holding it as fractions keeps the viewport on the hole at any frame
// width, instead of the hand-tuned offsets that had the portal running ~5px under the bezel on
// every side.
const FRAME_ASPECT = 2936 / 1450
const SCREEN = { left: 0.0552, top: 0.0259, width: 0.8897, height: 0.9482 }
const SCREEN_RADIUS = 34

// The aperture's proportions are an iPhone 14 Pro's (2.16 tall), so the portal renders at that
// device's viewport and is scaled into the hole. The preview then lays out the way a shopper's
// phone would rather than at whatever width happened to fit the mockup.
const VIEWPORT_WIDTH = 390

const frameHeight = FRAME_WIDTH * FRAME_ASPECT
const screen = {
  left: FRAME_WIDTH * SCREEN.left,
  top: frameHeight * SCREEN.top,
  width: FRAME_WIDTH * SCREEN.width,
  height: frameHeight * SCREEN.height
}
const scale = screen.width / VIEWPORT_WIDTH

// A laptop lid: 16:10 like the hardware, at a common desktop viewport. Same idea as the phone —
// render at the device's real width so the portal lays out as it would there.
const LID_WIDTH = 560
const LID_PADDING = 12
const BASE_WIDTH = LID_WIDTH * 1.14
const CHROME_HEIGHT = 22

const DESKTOP_VIEWPORT = { width: 1280, height: 800 }
const desktopScreenWidth = LID_WIDTH - LID_PADDING * 2
const desktopScale = desktopScreenWidth / DESKTOP_VIEWPORT.width
const desktopScreenHeight = DESKTOP_VIEWPORT.height * desktopScale

interface PortalFrameProps {
  portalView: keyof typeof portalViews
  device?: PreviewDevice
}

export const PortalFrame: React.FC<PortalFrameProps> = ({
  portalView,
  device = 'mobile'
}) =>
  device === 'desktop' ? (
    <DesktopFrame portalView={portalView} />
  ) : (
    <PhoneFrame portalView={portalView} />
  )

const PhoneFrame: React.FC<{ portalView: keyof typeof portalViews }> = ({
  portalView
}) => (
  // Isolated so the mockup's z-50 competes only inside the frame: it sits in the page's scroll
  // area, under a sticky tab bar it would otherwise paint over.
  <div
    className="relative isolate shrink-0"
    style={{ width: FRAME_WIDTH, height: frameHeight }}
  >
    <img
      src={iphoneMockup}
      alt=""
      className="pointer-events-none absolute inset-0 z-50 w-full"
    />
    <div
      className="absolute z-0 overflow-hidden bg-white"
      style={{
        left: screen.left,
        top: screen.top,
        width: screen.width,
        height: screen.height,
        borderRadius: SCREEN_RADIUS
      }}
    >
      <ScaledViewport
        portalView={portalView}
        width={VIEWPORT_WIDTH}
        height={screen.height / scale}
        scale={scale}
      />
    </div>
  </div>
)

// A laptop, with the portal inside a browser window: the chrome is the only place the portal
// title is visible at all, and the lid is what tells a merchant this is the wide layout.
const DesktopFrame: React.FC<{ portalView: keyof typeof portalViews }> = ({
  portalView
}) => {
  const title = usePortalBranding().title

  return (
    <div className="isolate flex shrink-0 flex-col items-center">
      <div
        className="rounded-t-xl bg-neutral-800 pb-2"
        style={{ width: LID_WIDTH, paddingInline: LID_PADDING }}
      >
        <div className="flex h-3 items-center justify-center">
          <span className="size-1 rounded-full bg-neutral-600" />
        </div>
        <div
          className="overflow-hidden rounded-sm bg-white"
          style={{ height: desktopScreenHeight }}
        >
          <div
            className="flex items-center gap-2 border-b border-gray-200 bg-gray-100 px-2"
            style={{ height: CHROME_HEIGHT }}
          >
            <div className="flex gap-1">
              <span className="size-1.5 rounded-full bg-gray-300" />
              <span className="size-1.5 rounded-full bg-gray-300" />
              <span className="size-1.5 rounded-full bg-gray-300" />
            </div>
            <span className="max-w-[200px] truncate rounded bg-white px-2 py-px text-[9px] text-gray-500">
              {title}
            </span>
          </div>
          <ScaledViewport
            portalView={portalView}
            width={DESKTOP_VIEWPORT.width}
            height={(desktopScreenHeight - CHROME_HEIGHT) / desktopScale}
            scale={desktopScale}
          />
        </div>
      </div>
      {/* The base, wider than the lid, with the notch a MacBook opens by. */}
      <div
        className="flex justify-center rounded-b-lg bg-gradient-to-b from-neutral-300 to-neutral-400"
        style={{ width: BASE_WIDTH, height: 10 }}
      >
        <span className="h-1 w-16 rounded-b-md bg-neutral-400" />
      </div>
    </div>
  )
}

interface ScaledViewportProps {
  portalView: keyof typeof portalViews
  width: number
  height: number
  scale: number
}

// The portal at the device's own viewport size, shrunk to whatever the frame gives it.
function ScaledViewport({
  portalView,
  width,
  height,
  scale
}: ScaledViewportProps) {
  const CurrentView = portalViews[portalView]

  return (
    <div
      className="overflow-y-auto"
      style={{
        width,
        height,
        transform: `scale(${scale})`,
        transformOrigin: 'top left'
      }}
    >
      <CurrentView />
    </div>
  )
}

export default PortalFrame
